/**
 * useUserInfo.ts — Thin adapter layer
 *
 * Re-exports auth functions from @nestingale/auth so that existing imports
 * (`import { ... } from '@/utils/useUserInfo'`) continue to work.
 *
 * App-specific functions (signOutUser) are kept here.
 */

// ─── Re-exports from @nestingale/auth ───────────────────────────────────────────────
export {
  getUserTypeFromUrlLs,
  getCustIdFromLS,
  removeCustIdFromLS,
  getEitherCustIdFromLs,
  getTempCustIdFromLS,
  setTempCustIdToLS,
  getStpRoomTypeFromLS,
  setStpRoomTypeSel,
  removeStpRoomTypeSel,
  getCustStyleProfileIdFromLS,
  setCustStyleProfileIdToLS,
  removeCustStyleProfileIdFromLS,
  setCurrentBrand,
  getCurrentBrand,
  setDirectCognitoCredentialsIntoLS,
  getDirectCognitoCredentialsFromLS,
  deleteDirectCognitoCredentialsFromLS,
  getCurrentUserCognitoCredentials,
  clearAllCognitoCredentials,
  deleteGoogleCredentials,
  getGoogleCredentialsFromLS,
  deleteAppleCredentialsFromLS,
  deleteFBKCredentialsFromLS,
  CART_TOKEN,
  GCART_TOKEN,
} from '@nestingale/auth';

export {
  getUserInfo,
  fetchCartDetails,
  getUserCartInfo,
  updateCartData,
  fetchFavorites,
  postFavorite,
  deleteFavorite,
} from '@nestingale/auth';

export type { CartInfo } from '@nestingale/auth';

// ─── App-specific: API_BASE_URL ────────────────────────────────────────────────
export const API_BASE_URL = 'https://nestingale.com';

// ─── App-specific: signOutUser ─────────────────────────────────────────────────
// This uses the app's axiosObj (with signing interceptors) and does app-specific
// redirect logic, so it stays in the consumer app.
import {
  deleteGoogleCredentials as _deleteGoogleCredentials,
  deleteFBKCredentialsFromLS as _deleteFBK,
  deleteDirectCognitoCredentialsFromLS as _deleteCog,
  clearAllCognitoCredentials as _clearAll,
  removeCustStyleProfileIdFromLS as _removeSpId,
  removeCustIdFromLS as _removeCustId,
  removeStpRoomTypeSel as _removeStpRoom,
  CART_TOKEN as _CART_TOKEN,
  GCART_TOKEN as _GCART_TOKEN,
} from '@nestingale/auth';
import { getAmplifyBackend } from '@nestingale/auth';
import { clearAuthSessionCookie } from '@nestingale/auth';
import axiosObj from '@/store/axios';

function baseURL() {
  if (typeof window !== 'undefined') {
    return `${location.protocol}//${location.host}`;
  }
  return '';
}

let BASE_URL = baseURL();

export async function signOutUser(toReset: boolean = false, redirectTo?: string) {
  // Clear all social & cognito credentials from localStorage
  _deleteGoogleCredentials();
  _deleteFBK();
  _deleteCog();

  try {
    // Use @nestingale/auth v6 signOut
    await getAmplifyBackend().signOut();
    console.log('User signed out');
  } catch (error) {
    console.error('Error signing out:', error);
  }

  // Clear Cognito credentials for all user types
  _clearAll();

  if (typeof window !== 'undefined') {
    try {
      _removeSpId();
      _removeCustId();
    } catch (e) {
      console.log('Failed to clear localStorage IDs');
    }

    _removeStpRoom();
    // @ts-ignore
    window.setCookieWithoutSecure(_CART_TOKEN, null, 10);
    // @ts-ignore
    window.setCookieWithoutSecure(_GCART_TOKEN, null, 10);
  }

  // Call signout API
  try {
    // Clear the server-side auth session cookie
    await clearAuthSessionCookie({ endpoint: '/showroom/api/auth/session' }).catch(() => {});

    const url = `${BASE_URL}/showroom/api/signoutuser`;
    const response = await axiosObj.get(url);
    if (response.data) {
      try {
        await getAmplifyBackend().signOut();
      } catch (error) {
        console.error('Error signing out:', error);
      }
      if (redirectTo) {
        window.location.href = redirectTo;
      } else if (toReset) {
        const currentUrl = window.location.href;
        const isDedicatedDomain = !window.location.pathname.startsWith('/showroom/');
        if (isDedicatedDomain) {
          window.location.href = `/login?next=` + currentUrl;
        } else {
          const vendor = window.location.pathname.split('/')[2] || 'nestingale';
          window.location.href = `/showroom/${vendor}/login?next=` + currentUrl;
        }
      } else {
        const isDedicatedDomain = !window.location.pathname.startsWith('/showroom/');
        if (isDedicatedDomain) {
          window.location.href = '/';
        } else {
          const vendor = window.location.pathname.split('/')[2] || 'nestingale';
          window.location.href = `/showroom/${vendor}`;
        }
      }
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching user details:', error);
    try {
      await getAmplifyBackend().signOut();
    } catch (err) {
      console.error('Error signing out:', err);
    }
    throw error;
  }
}
